import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { usePassportProvider } from "@/context";
import {
  Action,
  ControlType,
  useOnboardingAnalytics,
} from "@/context/AnalyticsProvider";
import { useIsOnboardingCompleted } from "@/hooks/useIsOnboardingCompleted";
import { cn } from "@/lib/utils";
import {} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { OnboardingStep } from "../constants";
import { OnboardingContent } from "./OnboardingContent";

export const OnboardingModal = ({
  isModalOpen,
  setIsModalOpen,
}: { isModalOpen: boolean; setIsModalOpen: (open: boolean) => void }) => {
  const [showOnboardingFlow, setShowOnboardingFlow] = useState(false);

  const { isLoggingIn, isInitializing } = usePassportProvider();
  const { isOnboardingCompleted } = useIsOnboardingCompleted();

  const { track } = useOnboardingAnalytics();

  useEffect(() => {
    if (!isModalOpen) return;

    track({
      action: Action.VIEWED,
      extras: {
        id: OnboardingStep.ONBOARDING_MODAL,
      },
    });
  }, [track, isModalOpen]);

  // Show onboarding flow if onboarding is not completed
  useEffect(() => {
    if (isLoggingIn) return;

    if (isInitializing || isOnboardingCompleted === false) {
      setShowOnboardingFlow(true);
      track({
        action: Action.VIEWED,
        extras: {
          id: OnboardingStep.ONBOARDING_MODAL_FLOW,
        },
      });
    }
  }, [isLoggingIn, isOnboardingCompleted, isInitializing, track]);

  useEffect(() => {
    if (isOnboardingCompleted && !showOnboardingFlow) setIsModalOpen(false);
  }, [isOnboardingCompleted, showOnboardingFlow, setIsModalOpen]);

  const handleCloseModal = useCallback(() => {
    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.ONBOARDING_MODAL,
        control: "close-modal-login",
        controlType: ControlType.BUTTON,
      },
    });
    setIsModalOpen(false);
  }, [track, setIsModalOpen]);

  const handleOnOpenChange = useCallback(() => {
    if (!showOnboardingFlow) return handleCloseModal();

    track({
      action: Action.PRESSED,
      extras: {
        id: OnboardingStep.ONBOARDING_MODAL,
        control: "close-modal-onboarding",
        controlType: ControlType.BUTTON,
      },
    });
    handleCloseModal();
  }, [showOnboardingFlow, handleCloseModal, track]);

  return (
    <Dialog open={isModalOpen} onOpenChange={handleOnOpenChange}>
      <DialogTitle className="sr-only">Onboarding Flow</DialogTitle>
      <DialogContent
        aria-describedby="OnboardingModal"
        className={cn(
          "h-dvh w-screen !rounded-2xl shadow-2xl shadow-black max-h-[50rem] max-w-md p-0 border-0 overflow-hidden [&>button:last-child]:hidden",
        )}
        overlayClassName="bg-white/30 backdrop-blur-md"
      >
        <OnboardingContent
          isModal
          isOnboardingCompleted={isOnboardingCompleted}
          handleCloseModal={handleCloseModal}
        />
      </DialogContent>
    </Dialog>
  );
};
